import { useState, useEffect, useMemo, useCallback, memo, useRef } from 'react';
import moment from 'moment';
import { useGlobal } from 'reactn';
import { throttle } from 'lodash';
import Card from '../../common/components/Card';
import DateRangePicker from '../../common/components/DateRangePicker';
import TextLoader from '../../common/components/TextLoader';
import { GET_TIMESHEET_WEEKLY_SNAPSHOT } from '../queries';
import { fetchTimesheetsForPeriod } from '../index';
import { calculateCost } from '../../project-manager/components/ProjectFinancialsDetailsTable';
import {
  Modal,
  TextInput,
  ActionIcon,
  Select,
  Table,
  Menu,
  Checkbox,
  Button,
  Popover,
  Radio,
  Stack,
} from '@mantine/core';
import client from '../../../api/graphql/client';
import gql from 'graphql-tag';
import { useQuery } from 'react-apollo';
import { modals } from '@mantine/modals';
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  getGroupedRowModel,
  getExpandedRowModel,
  flexRender,
} from '@tanstack/react-table';
import styled from 'styled-components';
import Icon from '../../common/components/Icon';
import Tooltip from '../../common/components/Tooltip';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';

// Column categories for better organization
const COLUMN_CATEGORIES = {
  'Time Info': ['endDate', 'taskdate', 'hours'],
  'Employee Info': ['lastName', 'firstName', 'name', 'payrollId', 'email', 'role', 'supervisor'],
  'Project Info': ['clientName', 'project', 'projectCode', 'milestone', 'deliverable', 'bu'],
  Financial: ['rate', 'totalCost'],
  Status: ['status', 'approvers', 'approvedOn'],
  Other: ['comment', 'roles'],
};

// Optimized Column Selector Component
const ColumnSelector = memo(({ table, columnOrder, onColumnOrderChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [, forceUpdate] = useState({});

  // Get visible columns count
  const visibleCount = table.getAllLeafColumns().filter(col => col.getIsVisible()).length;
  const totalCount = table.getAllLeafColumns().length;

  // Filter columns based on search and category
  const filteredColumns = useMemo(() => {
    let cols = table.getAllLeafColumns();

    // Apply search filter
    if (searchTerm) {
      cols = cols.filter(col =>
        col.columnDef.header.toLowerCase().includes(searchTerm.toLowerCase()),
      );
    }

    // Apply category filter
    if (selectedCategory !== 'All') {
      const categoryColumns = COLUMN_CATEGORIES[selectedCategory] || [];
      cols = cols.filter(col => categoryColumns.includes(col.id));
    }

    return cols;
  }, [table, searchTerm, selectedCategory]);

  // Group columns by category
  const groupedColumns = useMemo(() => {
    if (selectedCategory !== 'All') {
      return { [selectedCategory]: filteredColumns };
    }

    const grouped = {};
    Object.entries(COLUMN_CATEGORIES).forEach(([category, columnIds]) => {
      const categoryColumns = filteredColumns.filter(col => columnIds.includes(col.id));
      if (categoryColumns.length > 0) {
        grouped[category] = categoryColumns;
      }
    });

    return grouped;
  }, [filteredColumns, selectedCategory]);

  // Handle drag end for column reordering
  const handleDragEnd = useCallback(
    result => {
      if (!result.destination) return;

      const { source, destination } = result;
      if (source.index === destination.index) return;

      // Get all columns in their current order
      const currentOrder =
        columnOrder.length > 0 ? columnOrder : table.getAllLeafColumns().map(c => c.id);
      const newOrder = Array.from(currentOrder);
      const [reorderedItem] = newOrder.splice(source.index, 1);
      newOrder.splice(destination.index, 0, reorderedItem);

      onColumnOrderChange(newOrder);
    },
    [columnOrder, table, onColumnOrderChange],
  );

  // Quick actions
  const handleShowAll = () => {
    table.getAllLeafColumns().forEach(col => col.toggleVisibility(true));
    forceUpdate({});
  };

  const handleHideAll = () => {
    table.getAllLeafColumns().forEach(col => col.toggleVisibility(false));
    forceUpdate({});
  };

  const handleResetToDefault = () => {
    // Default visible columns
    const defaultVisible = [
      'endDate',
      'status',
      'lastName',
      'firstName',
      'clientName',
      'project',
      'taskdate',
      'hours',
      'totalCost',
    ];
    table.getAllLeafColumns().forEach(col => {
      col.toggleVisibility(defaultVisible.includes(col.id));
    });
    onColumnOrderChange([]);
    forceUpdate({});
  };

  return (
    <div className="relative">
      <Button
        variant="default"
        onClick={() => setIsOpen(!isOpen)}
        rightIcon={<Icon type={isOpen ? 'up' : 'down'} />}
      >
        Columns ({visibleCount}/{totalCount})
      </Button>

      {isOpen && (
        <>
          {/* Backdrop */}
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />

          {/* Column Selector Panel */}
          <div className="absolute top-full left-0 mt-1 w-96 max-h-[600px] bg-white rounded-lg shadow-xl border border-gray-200 z-50 flex flex-col">
            {/* Header */}
            <div className="p-4 border-b border-gray-200">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold">Manage Columns</h3>
                <ActionIcon onClick={() => setIsOpen(false)} variant="subtle">
                  <Icon type="close" />
                </ActionIcon>
              </div>

              {/* Search */}
              <TextInput
                placeholder="Search columns..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                icon={<Icon type="search" />}
                rightSection={
                  searchTerm && (
                    <ActionIcon size="xs" onClick={() => setSearchTerm('')} variant="subtle">
                      <Icon type="close" />
                    </ActionIcon>
                  )
                }
              />

              {/* Category Filter */}
              <div className="flex flex-wrap gap-1 mt-3">
                <Button
                  size="sm"
                  variant={selectedCategory === 'All' ? 'filled' : 'default'}
                  onClick={() => setSelectedCategory('All')}
                >
                  All
                </Button>
                {Object.keys(COLUMN_CATEGORIES).map(category => (
                  <Button
                    key={category}
                    size="sm"
                    variant={selectedCategory === category ? 'filled' : 'default'}
                    onClick={() => setSelectedCategory(category)}
                  >
                    {category}
                  </Button>
                ))}
              </div>

              {/* Quick Actions */}
              <div className="flex gap-2 mt-3">
                <Button size="sm" variant="default" onClick={handleShowAll}>
                  Show All
                </Button>
                <Button size="sm" variant="default" onClick={handleHideAll}>
                  Hide All
                </Button>
                <Button size="sm" variant="default" onClick={handleResetToDefault}>
                  Reset to Default
                </Button>
              </div>
            </div>

            {/* Column List */}
            <div className="flex-1 overflow-y-auto p-4">
              {selectedCategory === 'All' ? (
                // When showing all columns, use drag and drop for reordering
                <DragDropContext onDragEnd={handleDragEnd}>
                  <Droppable droppableId="column-list">
                    {provided => (
                      <div {...provided.droppableProps} ref={provided.innerRef}>
                        {(columnOrder.length > 0
                          ? columnOrder
                          : table.getAllLeafColumns().map(c => c.id)
                        ).map((columnId, index) => {
                          const column = table.getAllLeafColumns().find(c => c.id === columnId);
                          if (!column) return null;

                          // Check if column matches search
                          if (
                            searchTerm &&
                            !column.columnDef.header
                              .toLowerCase()
                              .includes(searchTerm.toLowerCase())
                          ) {
                            return null;
                          }

                          return (
                            <Draggable key={column.id} draggableId={column.id} index={index}>
                              {(provided, snapshot) => (
                                <div
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  className={`flex items-center justify-between p-2 rounded transition-colors mb-1 ${
                                    snapshot.isDragging
                                      ? 'bg-blue-50 shadow-lg'
                                      : 'hover:bg-gray-50'
                                  }`}
                                >
                                  <Checkbox
                                    checked={column.getIsVisible()}
                                    onChange={e => {
                                      column.getToggleVisibilityHandler()(e);
                                      forceUpdate({});
                                    }}
                                    label={
                                      <span className="text-sm">
                                        {column.columnDef.header}
                                        {searchTerm && (
                                          <span className="ml-2 text-xs text-blue-500">
                                            (matched)
                                          </span>
                                        )}
                                      </span>
                                    }
                                  />
                                  <div {...provided.dragHandleProps}>
                                    <Icon
                                      type="menu"
                                      className="text-gray-400 cursor-move hover:text-gray-600"
                                      title="Drag to reorder"
                                    />
                                  </div>
                                </div>
                              )}
                            </Draggable>
                          );
                        })}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>
              ) : (
                // When showing a specific category, no drag and drop
                Object.entries(groupedColumns).map(([category, categoryColumns]) => (
                  <div key={category} className="mb-4">
                    <h4 className="text-sm font-semibold text-gray-600 mb-2">{category}</h4>
                    <div className="space-y-1">
                      {categoryColumns.map(column => (
                        <div
                          key={column.id}
                          className="flex items-center justify-between p-2 rounded hover:bg-gray-50 transition-colors"
                        >
                          <Checkbox
                            checked={column.getIsVisible()}
                            onChange={e => {
                              column.getToggleVisibilityHandler()(e);
                              forceUpdate({});
                            }}
                            label={
                              <span className="text-sm">
                                {column.columnDef.header}
                                {searchTerm &&
                                  column.columnDef.header
                                    .toLowerCase()
                                    .includes(searchTerm.toLowerCase()) && (
                                    <span className="ml-2 text-xs text-blue-500">(matched)</span>
                                  )}
                              </span>
                            }
                          />
                        </div>
                      ))}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {categoryColumns.filter(c => c.getIsVisible()).length} of{' '}
                      {categoryColumns.length} visible
                    </div>
                  </div>
                ))
              )}

              {filteredColumns.length === 0 && (
                <div className="text-center text-gray-500 py-8">
                  No columns found matching "{searchTerm}"
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-gray-200 bg-gray-50">
              <div className="flex justify-between items-center">
                <div className="text-sm text-gray-600">
                  {visibleCount} of {totalCount} columns visible
                </div>
                {selectedCategory === 'All' && (
                  <div className="text-xs text-gray-500">
                    <Icon type="info-circle" className="mr-1" />
                    Drag columns to reorder
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
});

// Optimized date formatter with cache
const formatDate = (() => {
  const cache = new Map();
  const MAX_CACHE_SIZE = 1000;

  return dateStr => {
    if (cache.has(dateStr)) return cache.get(dateStr);

    const year = dateStr.substring(0, 4);
    const month = dateStr.substring(4, 6);
    const day = dateStr.substring(6, 8);
    const date = new Date(year, month - 1, day);
    const formatted = date.toLocaleDateString('fr-CA');

    // Implement LRU cache eviction
    if (cache.size >= MAX_CACHE_SIZE) {
      const firstKey = cache.keys().next().value;
      cache.delete(firstKey);
    }

    cache.set(dateStr, formatted);
    return formatted;
  };
})();

// Optimized data processing with caching
const createGridData = timesheets => {
  const days = ['monTask', 'tueTask', 'wedTask', 'thuTask', 'friTask', 'satTask', 'sunTask'];
  const result = [];
  const userCache = new Map();

  for (const ts of timesheets) {
    const tsId = ts._id;
    const tsStatus = ts.status;
    const tsEndDate = moment(ts.endDate).format('MM-DD-YYYY');
    const userId = ts.user?._id;

    // Cache user data to avoid repeated calculations
    let userData = userCache.get(userId);
    if (!userData) {
      const user = ts.user || {};
      userData = {
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        name: `${user.firstName || ''} ${user.lastName || ''}`.trim(),
        role: user.role,
        email: user.emails?.[0]?.address,
        payrollId: user.payrollId,
        defaultRate: user.defaultRate,
        supervisor: user.supervisor
          ? `${user.supervisor.firstName || ''} ${user.supervisor.lastName || ''}`.trim()
          : undefined,
      };
      userCache.set(userId, userData);
    }

    // Pre-calculate approval info
    let approvers, approvedOn;
    if (tsStatus === 'Approved' && ts.approvalsRequired) {
      approvers =
        ts.approvalsRequired
          .map(a => a.approvedBy)
          .filter(Boolean)
          .join(', ') || undefined;

      const latestApproval = ts.approvalsRequired.reduce(
        (latest, current) =>
          !latest || new Date(current.approvedOn) > new Date(latest.approvedOn) ? current : latest,
        null,
      );

      approvedOn = latestApproval?.approvedOn
        ? moment(latestApproval.approvedOn).format('MM-DD-YYYY')
        : moment(ts.updatedAt).format('MM-DD-YYYY');
    }

    // Process line items
    for (const li of ts.lineItems || []) {
      const project = li.project || {};
      const deliverable = li.deliverable || {};

      // Build BU code
      const projectCode = project.accountingCode || '';
      const projectSuffix = project.accountingCodeSuffix || '';
      let bu = `${projectCode}-${projectSuffix}`;

      if (deliverable.accountingCode && deliverable.accountingCodeSuffix) {
        bu = `${deliverable.accountingCode}-${deliverable.accountingCodeSuffix}`;
      }

      const projectRoles = project.roles || [];
      const resources = project.resources || [];
      const resourceRole = resources.find(r => r.email === userData.email)?.role;

      // Create base object
      const baseObj = {
        timesheetId: tsId,
        status: tsStatus,
        endDate: tsEndDate,
        ...userData,
        role: resourceRole || userData.role,
        clientName: project.client?.shortName,
        project: project.name,
        projectCode: project.projectCode,
        roles: projectRoles.length,
        bu,
        milestone: deliverable.milestoneName,
        deliverable: deliverable.deliverableName,
        approvedOn,
        approvers,
      };

      // Process tasks
      for (let i = 0; i < days.length; i++) {
        const task = li[days[i]];
        if (task?.date && task.hours) {
          const totalCost = calculateCost({
            email: userData.email,
            hours: task.hours,
            resources,
            roles: projectRoles,
            userRole: userData.role,
            defaultRate: userData.defaultRate,
          });

          result.push({
            ...baseObj,
            taskdate: formatDate(task.date),
            hours: task.hours,
            comment: task.comment,
            totalCost,
            rate: task.hours ? totalCost / task.hours : 0,
          });
        }
      }
    }
  }

  return result;
};

// List of all columns for use in Select components
const ALL_COLUMNS = [
  { value: 'endDate', label: 'Week Ending' },
  { value: 'status', label: 'Status' },
  { value: 'lastName', label: 'Last Name' },
  { value: 'firstName', label: 'First Name' },
  { value: 'payrollId', label: 'Payroll ID' },
  { value: 'email', label: 'Email' },
  { value: 'clientName', label: 'Client Name' },
  { value: 'project', label: 'Project' },
  { value: 'projectCode', label: 'Project Code' },
  { value: 'milestone', label: 'Milestone' },
  { value: 'deliverable', label: 'Deliverable' },
  { value: 'taskdate', label: 'Task Date' },
  { value: 'hours', label: 'Hours' },
  { value: 'role', label: 'Role' },
  { value: 'rate', label: 'Rate' },
  { value: 'name', label: 'Name' },
  { value: 'totalCost', label: 'Total Cost' },
  { value: 'supervisor', label: 'Supervisor' },
  { value: 'approvers', label: 'Approvers' },
  { value: 'approvedOn', label: 'Approved On' },
  { value: 'comment', label: 'Comment' },
  { value: 'roles', label: 'Roles' },
  { value: 'bu', label: 'BU' },
];

const UPDATE_USER_SETTINGS = gql`
  mutation UpdateUserSettings($id: ID!, $input: UpdateUserSettingsInput!) {
    updateUserSettings(_id: $id, input: $input)
  }
`;

const GET_MY_SETTINGS = gql`
  query GET_MY_SETTINGS {
    me {
      _id
      settings {
        timesheetDailySnapshotOverrides
        timesheetDailySnapshotReports
      }
    }
  }
`;

const TableWrapper = styled.div`
  height: 700px;
  width: 100%;
  overflow: auto;
  background: white;
  border: 1px solid #e8e8e8;
  border-radius: 4px;
  position: relative;

  /* Enhanced tooltip visibility */
  .custom-tooltip-overlay {
    .ant-tooltip-inner {
      background-color: rgba(0, 0, 0, 0.9);
      color: white;
      font-size: 14px;
      padding: 8px 12px;
      font-weight: 500;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    }

    .ant-tooltip-arrow-content {
      background-color: rgba(0, 0, 0, 0.9);
    }
  }

  table {
    width: 100%;
    border-collapse: collapse;
    font-size: 12px;
    table-layout: fixed;
  }

  thead {
    position: sticky;
    top: 0;
    z-index: 10;
    background: #fafafa;
  }

  th {
    border: 1px solid #e8e8e8;
    padding: 8px 16px 8px 12px;
    text-align: left;
    font-weight: 600;
    white-space: nowrap;
    user-select: none;
    position: relative;
    overflow: visible;
    text-overflow: ellipsis;
    cursor: move;

    &.dragging {
      opacity: 0.5;
      background: #e6f7ff;
    }

    &.drag-over {
      background: #f0f0f0;
      border-left: 3px solid #1890ff;
    }
  }

  td {
    border: 1px solid #e8e8e8;
    padding: 8px 12px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 0;
  }

  tr:hover {
    background: #f5f5f5;
  }

  .column-resizer {
    position: absolute;
    right: -5px;
    top: 0;
    height: 100%;
    width: 10px;
    cursor: col-resize;
    z-index: 20;
    background: transparent;

    /* Larger hit area */
    &::before {
      content: '';
      position: absolute;
      left: -5px;
      right: -5px;
      top: 0;
      bottom: 0;
    }

    /* Visual resize handle - always visible but subtle */
    &::after {
      content: '';
      position: absolute;
      left: 50%;
      transform: translateX(-50%);
      top: 20%;
      bottom: 20%;
      width: 2px;
      background: #d9d9d9;
      border-radius: 1px;
      transition: all 0.2s ease;
      /* Add a dotted pattern for better visibility */
      background-image: linear-gradient(
        to bottom,
        #d9d9d9 0%,
        #d9d9d9 40%,
        transparent 40%,
        transparent 60%,
        #d9d9d9 60%,
        #d9d9d9 100%
      );
      background-size: 2px 8px;
    }

    &:hover::after {
      background: #1890ff;
      width: 3px;
      top: 10%;
      bottom: 10%;
      box-shadow: 0 0 0 1px rgba(24, 144, 255, 0.2);
      background-image: linear-gradient(
        to bottom,
        #1890ff 0%,
        #1890ff 40%,
        transparent 40%,
        transparent 60%,
        #1890ff 60%,
        #1890ff 100%
      );
      background-size: 3px 8px;
    }

    &.resizing::after {
      background: #0958d9;
      width: 3px;
      background-image: none;
    }
  }

  /* Show resize handle more prominently on header hover */
  th:hover .column-resizer::after {
    background: #bfbfbf;
  }

  /* Cursor feedback during resize */
  table.resizing-active {
    cursor: col-resize;
    user-select: none;
  }

  /* Prevent th cursor change during resize */
  table.resizing-active th {
    cursor: col-resize !important;
  }

  .sortable-header {
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 4px;
  }

  .grouped-cell {
    font-weight: 600;
    background: #f5f5f5;
    transition: background-color 0.2s;

    &:hover {
      background: #e8e8e8;
    }
  }

  .aggregated-cell {
    font-style: italic;
    color: #666;
  }

  /* Group, sort and filter icon visibility on hover */
  th .anticon-bar-chart,
  th .anticon-sort,
  th .anticon-filter {
    opacity: 0;
    transition: opacity 0.2s, color 0.2s;
  }

  th:hover .anticon-bar-chart,
  th:hover .anticon-filter,
  th:hover .anticon-sort {
    opacity: 0.4;
  }

  /* Direct hover on icons */
  th .anticon-bar-chart:hover,
  th .anticon-filter:hover,
  th .anticon-sort:hover {
    opacity: 0.8 !important;
    color: #262626;
  }

  /* Active state styling */
  th .anticon-bar-chart.active,
  th .anticon-sort.sorted,
  th .anticon-filter.active {
    opacity: 1 !important;
    color: #1890ff;
  }

  th .anticon-bar-chart.active:hover,
  th .anticon-sort.sorted:hover,
  th .anticon-filter.active:hover {
    opacity: 1 !important;
    color: #0958d9;
  }

  th .anticon-bar-chart.active {
    opacity: 1 !important;
    color: #1890ff;
  }

  /* Sort icon visibility when sorted */
  th .sort-icon.sorted {
    opacity: 1 !important;
    color: #1890ff;
  }
`;

const OverrideRuleEditor = memo(({ initialConditions, initialOverrideFields, onSave }) => {
  const [conditions, setConditions] = useState(initialConditions);
  const [overrideFields, setOverrideFields] = useState(initialOverrideFields);
  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-4 border-b mb-2">
        <div className="font-semibold text-lg">Conditions</div>
        <div className="flex flex-col gap-2">
          {conditions.map((cond, idx) => (
            <div
              key={idx}
              className="flex flex-row items-end bg-gray-50 rounded border relative gap-2"
            >
              <div className="flex flex-col w-1/2">
                <Select
                  placeholder="Select field"
                  data={ALL_COLUMNS}
                  value={cond.field}
                  onChange={field => {
                    const newConds = [...conditions];
                    newConds[idx].field = field;
                    setConditions(newConds);
                  }}
                  searchable
                  clearable
                />
              </div>
              <div className="flex flex-col w-1/2">
                <TextInput
                  placeholder="Value"
                  value={cond.value}
                  onChange={e => {
                    const newConds = [...conditions];
                    newConds[idx].value = e.target.value;
                    setConditions(newConds);
                  }}
                  disabled={!cond.field}
                />
              </div>
              <ActionIcon
                color="red"
                variant="subtle"
                onClick={() => {
                  setConditions(conditions.filter((_, i) => i !== idx));
                }}
                title="Remove Condition"
                className="hover:bg-red-100 hover:text-red-700 transition h-9 w-9 flex items-center justify-center"
                style={{ minHeight: 36, minWidth: 36 }}
              >
                <span className="text-lg font-bold">×</span>
              </ActionIcon>
            </div>
          ))}
        </div>
        <Button
          size="sm"
          variant="default"
          onClick={() => setConditions([...conditions, { field: '', value: '' }])}
          className="w-fit mt-2 self-center"
        >
          + Add Condition
        </Button>
      </div>
      <div className="flex flex-col md:flex-row gap-4 items-end border-b pb-6 mb-2">
        <div className="flex-1 flex flex-col gap-2">
          <div className="font-semibold text-lg">Override Fields</div>
          {overrideFields.map((ovr, idx) => (
            <div
              key={idx}
              className="flex flex-row items-end bg-gray-50 rounded border relative gap-2 mb-2"
            >
              <div className="flex flex-col w-1/2">
                <Select
                  placeholder="Select field to override"
                  data={ALL_COLUMNS}
                  value={ovr.field}
                  onChange={field => {
                    const newOvr = [...overrideFields];
                    newOvr[idx].field = field;
                    setOverrideFields(newOvr);
                  }}
                  searchable
                  clearable
                />
              </div>
              <div className="flex flex-col w-1/2">
                <TextInput
                  placeholder="Override value"
                  value={ovr.value}
                  onChange={e => {
                    const newOvr = [...overrideFields];
                    newOvr[idx].value = e.target.value;
                    setOverrideFields(newOvr);
                  }}
                  disabled={!ovr.field}
                />
              </div>
              <ActionIcon
                color="red"
                variant="subtle"
                onClick={() => {
                  setOverrideFields(overrideFields.filter((_, i) => i !== idx));
                }}
                title="Remove Override Field"
                className="hover:bg-red-100 hover:text-red-700 transition h-9 w-1/2 flex items-center justify-center"
                style={{ minHeight: 36, minWidth: 36 }}
              >
                <span className="text-lg font-bold">×</span>
              </ActionIcon>
            </div>
          ))}
          <Button
            size="sm"
            variant="default"
            onClick={() => setOverrideFields([...overrideFields, { field: '', value: '' }])}
            className="w-fit mt-2 self-center"
          >
            + Add Override Field
          </Button>
        </div>
      </div>
      <div className="flex flex-row justify-end mb-4 gap-2">
        <Button
          variant="filled"
          disabled={
            conditions.length === 0 ||
            overrideFields.length === 0 ||
            conditions.some(c => !c.field || !c.value) ||
            overrideFields.some(o => !o.field || o.value === undefined || o.value === '')
          }
          onClick={() => onSave(conditions, overrideFields)}
        >
          Save
        </Button>
      </div>
    </div>
  );
});

// Filter Popover Component
const FilterPopover = memo(({ column }) => {
  const [opened, setOpened] = useState(false);
  const [filterType, setFilterType] = useState('contains');
  const [filterValue, setFilterValue] = useState('');

  // Get current filter value if exists
  useEffect(() => {
    const currentFilter = column.getFilterValue();
    if (currentFilter && typeof currentFilter === 'object') {
      setFilterType(currentFilter.type || 'contains');
      setFilterValue(currentFilter.value || '');
    } else if (currentFilter) {
      setFilterValue(String(currentFilter));
    }
  }, [column]);

  const handleApplyFilter = () => {
    if (filterValue) {
      column.setFilterValue({ type: filterType, value: filterValue });
    } else {
      column.setFilterValue(undefined);
    }
    setOpened(false);
  };

  const handleClearFilter = () => {
    column.setFilterValue(undefined);
    setFilterValue('');
    setFilterType('contains');
    setOpened(false);
  };

  const isFilterActive = !!column.getFilterValue();

  return (
    <Popover
      opened={opened}
      onChange={setOpened}
      position="bottom-start"
      width={250}
      shadow="md"
      withinPortal={false}
      offset={8}
    >
      <Popover.Target>
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            marginLeft: '4px',
          }}
        >
          <Tooltip
            title={
              isFilterActive
                ? `Filter is active on ${column.columnDef.header}`
                : `Filter by ${column.columnDef.header}`
            }
            placement="top"
            overlayStyle={{
              fontSize: '14px',
              maxWidth: '300px',
            }}
            overlayClassName="custom-tooltip-overlay"
          >
            <Icon
              type="filter"
              onClick={e => {
                e.stopPropagation();
                setOpened(!opened);
              }}
              style={{
                fontSize: '12px',
                cursor: 'pointer',
              }}
              className={`anticon-filter ${isFilterActive ? 'active' : ''}`}
              draggable={false}
              onMouseDown={e => e.stopPropagation()}
            />
          </Tooltip>
        </span>
      </Popover.Target>

      <Popover.Dropdown>
        <div className="p-2">
          <Radio.Group value={filterType} onChange={setFilterType} className="mb-3">
            <Stack spacing="xs">
              <Radio value="contains" label="Contains" />
              <Radio value="notContains" label="Not contains" />
              <Radio value="equals" label="Equals" />
              <Radio value="notEqual" label="Not equal" />
              <Radio value="startsWith" label="Starts with" />
              <Radio value="endsWith" label="Ends with" />
            </Stack>
          </Radio.Group>

          <TextInput
            placeholder="Filter value..."
            value={filterValue}
            onChange={e => setFilterValue(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                handleApplyFilter();
              }
            }}
            className="mb-3"
          />

          <div className="flex gap-2">
            <Button size="xs" variant="filled" onClick={handleApplyFilter} fullWidth>
              Apply
            </Button>
            <Button size="xs" variant="default" onClick={handleClearFilter} fullWidth>
              Clear
            </Button>
          </div>
        </div>
      </Popover.Dropdown>
    </Popover>
  );
});

// Optimized Row component with memo
const TableRow = memo(
  ({ row }) => {
    return (
      <tr key={row.id}>
        {row.getVisibleCells().map(cell => (
          <td
            key={cell.id}
            className={cell.getIsGrouped() ? 'grouped-cell' : ''}
            onClick={cell.getIsGrouped() ? row.getToggleExpandedHandler() : undefined}
            style={{
              width: `${cell.column.getSize()}px`,
              minWidth: `${cell.column.getSize()}px`,
              maxWidth: `${cell.column.getSize()}px`,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              ...(cell.getIsGrouped() ? { cursor: 'pointer' } : {}),
            }}
          >
            {cell.getIsGrouped() ? (
              // Grouped cell
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <span style={{ display: 'flex', alignItems: 'center' }}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </span>
                <span
                  style={{
                    fontSize: '11px',
                    color: '#666',
                    marginLeft: '12px',
                    fontWeight: 'normal',
                  }}
                >
                  ({row.subRows.length})
                </span>
              </div>
            ) : cell.getIsPlaceholder() ? null : (
              // Normal cell
              flexRender(cell.column.columnDef.cell, cell.getContext())
            )}
          </td>
        ))}
      </tr>
    );
  },
  (prevProps, nextProps) => {
    // Only re-render if row id or expanded state changes
    return (
      prevProps.row.id === nextProps.row.id &&
      prevProps.row.getIsExpanded() === nextProps.row.getIsExpanded()
    );
  },
);

const CustomReports = () => {
  const [config] = useGlobal('config');
  const dailySnapshotOverridesEnabled = config['daily-snapshot-overrides'] === 'true';
  const { data, refetch } = useQuery(GET_MY_SETTINGS);
  const userId = data?.me?._id;
  const userSettings = data?.me?.settings;
  const [loading, setLoading] = useState(false);
  const [loadingText, setLoadingText] = useState(null);
  const [startDate, setStartDate] = useState(moment().startOf('isoWeek').format('YYYYMMDD'));
  const [endDate, setEndDate] = useState(moment().endOf('isoWeek').format('YYYYMMDD'));
  const [rowData, setRowData] = useState([]);
  const [overrides, setOverrides] = useState([]);
  const [unsavedOverrides, setUnsavedOverrides] = useState([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const moveOverride = (fromIndex, toIndex) => {
    setOverrides(prev => {
      if (toIndex < 0 || toIndex >= prev.length || fromIndex === toIndex) return prev;
      const next = [...prev];
      const [item] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, item);
      return next;
    });
  };
  const [modalOpened, setModalOpened] = useState(false);

  // Report configuration state
  const [savedReports, setSavedReports] = useState([]);
  const [activeReport, setActiveReport] = useState(null);
  const [hasUnsavedReportChanges, setHasUnsavedReportChanges] = useState(false);
  const [reportTitleModalOpened, setReportTitleModalOpened] = useState(false);
  const [reportSaveMode, setReportSaveMode] = useState('new'); // 'new' or 'update'
  const [editingReportTitle, setEditingReportTitle] = useState('');

  // Table state
  const [columnVisibility, setColumnVisibility] = useState({});
  const [columnOrder, setColumnOrder] = useState([]);
  const [sorting, setSorting] = useState([]);
  const [grouping, setGrouping] = useState([]);
  const [columnSizing, setColumnSizing] = useState({});
  const [columnSizingInfo, setColumnSizingInfo] = useState({});
  const [columnFilters, setColumnFilters] = useState([]);

  // Virtual scrolling state
  const [visibleRange, setVisibleRange] = useState({ start: 0, end: 50 });
  const scrollContainerRef = useRef(null);
  const ROW_HEIGHT = 40; // Approximate row height in pixels

  // Drag state for column headers
  const [draggedColumn, setDraggedColumn] = useState(null);
  const [dragOverColumn, setDragOverColumn] = useState(null);

  // Column definitions for TanStack Table
  const columns = useMemo(
    () =>
      ALL_COLUMNS.map(col => {
        // Calculate minimum width based on header content
        // Base calculation: text width estimate + padding + icons
        const headerTextLength = col.label.length;
        const avgCharWidth = 8; // Average character width in pixels
        const padding = 28; // Left (12px) + Right (16px) padding
        const iconWidth = 16; // Width per icon (icon + margin)
        const sortIconSpace = iconWidth; // All columns are sortable
        const groupIconSpace = iconWidth; // All columns are groupable
        const bufferSpace = 10; // Additional buffer for comfortable viewing

        // Calculate minimum width needed for header
        const calculatedMinWidth = Math.max(
          headerTextLength * avgCharWidth + padding + sortIconSpace + groupIconSpace + bufferSpace,
          60, // Absolute minimum to ensure usability
        );

        return {
          id: col.value,
          accessorKey: col.value,
          header: col.label,
          enableGrouping: true,
          enableResizing: true,
          enableColumnFilter: true,
          filterFn: (row, columnId, filterValue) => {
            const value = row.getValue(columnId);
            if (!filterValue) return true;

            const cellValue = String(value || '').toLowerCase();

            // Handle object filter with type and value
            if (typeof filterValue === 'object' && filterValue.type && filterValue.value) {
              const searchValue = String(filterValue.value).toLowerCase();

              switch (filterValue.type) {
                case 'contains':
                  return cellValue.includes(searchValue);
                case 'notContains':
                  return !cellValue.includes(searchValue);
                case 'equals':
                  return cellValue === searchValue;
                case 'notEqual':
                  return cellValue !== searchValue;
                case 'startsWith':
                  return cellValue.startsWith(searchValue);
                case 'endsWith':
                  return cellValue.endsWith(searchValue);
                default:
                  return cellValue.includes(searchValue);
              }
            }

            // Default contains filter
            return cellValue.includes(String(filterValue).toLowerCase());
          },
          // Set appropriate default widths based on content type
          // Adding extra width to accommodate sort and group icons
          size: (() => {
            switch (col.value) {
              case 'endDate':
              case 'taskdate':
              case 'approvedOn':
                return 120; // Increased from 100
              case 'status':
                return 110; // Increased from 80 to prevent icon overflow
              case 'hours':
              case 'rate':
              case 'roles':
                return 100; // Increased from 80
              case 'payrollId':
              case 'projectCode':
              case 'bu':
                return 120; // Increased from 100
              case 'lastName':
              case 'firstName':
                return 140; // Increased from 120
              case 'email':
              case 'project':
              case 'deliverable':
              case 'supervisor':
              case 'approvers':
                return 220; // Increased from 200
              case 'clientName':
              case 'milestone':
                return 170; // Increased from 150
              case 'name':
                return 200; // Increased from 180
              case 'totalCost':
                return 120; // Increased from 100
              case 'comment':
                return 270; // Increased from 250
              default:
                return 170; // Increased from 150
            }
          })(),
          minSize: calculatedMinWidth, // Dynamic minimum based on header content
          maxSize: 500, // Maximum column width
          // Add aggregation for numeric columns
          ...(col.value === 'hours' || col.value === 'totalCost' || col.value === 'rate'
            ? {
                aggregationFn: 'sum',
                cell: ({ row, getValue }) => {
                  if (row.getIsGrouped()) {
                    // Calculate sum for grouped rows
                    const total = row.subRows.reduce((sum, subRow) => {
                      return sum + (subRow.getValue(col.value) || 0);
                    }, 0);
                    if (col.value === 'totalCost') return `$${total.toFixed(2)}`;
                    if (col.value === 'rate') return `$${total.toFixed(2)}`;
                    return total.toFixed(2);
                  }
                  const value = getValue();
                  if (col.value === 'totalCost') return value ? `$${value.toFixed(2)}` : '';
                  if (col.value === 'rate') return value ? `$${value.toFixed(2)}` : '';
                  return value || '';
                },
              }
            : {}),
          // Keep default cell rendering for non-numeric columns
          // The grouping count is already shown in the group header via TableRow component
        };
      }),
    [],
  );

  // Apply overrides to data with optimized lookup
  const processedData = useMemo(() => {
    if (overrides.length === 0) return rowData;

    // Build index for O(1) lookups
    const overrideMap = new Map();
    overrides.forEach(ovr => {
      const key = ovr.conditions
        .map(c => `${c.field}:${c.value}`)
        .sort()
        .join('|');
      if (!overrideMap.has(key)) {
        overrideMap.set(key, []);
      }
      overrideMap.get(key).push(ovr);
    });

    return rowData.map(row => {
      // Check for matching overrides using index
      const matchingOverrides = [];
      overrideMap.forEach((ovrList, key) => {
        const conditions = key.split('|').map(cond => {
          const [field, value] = cond.split(':');
          return { field, value };
        });

        if (conditions.every(cond => String(row[cond.field]) === cond.value)) {
          matchingOverrides.push(...ovrList);
        }
      });

      if (matchingOverrides.length === 0) return row;

      // Merge all override objects, later overrides take precedence
      const mergedOverride = matchingOverrides.reduce(
        (acc, ovr) => ({ ...acc, ...ovr.override }),
        {},
      );
      return { ...row, ...mergedOverride };
    });
  }, [rowData, overrides]);

  // Throttled scroll handler for virtual scrolling
  const handleScroll = useMemo(() => {
    const throttledHandler = throttle((scrollTop, clientHeight, dataLength) => {
      const visibleStart = Math.floor(scrollTop / ROW_HEIGHT);
      const visibleEnd = Math.ceil((scrollTop + clientHeight) / ROW_HEIGHT);

      setVisibleRange({
        start: Math.max(0, visibleStart - 5), // 5 rows buffer
        end: Math.min(dataLength, visibleEnd + 5),
      });
    }, 50);

    return e => {
      if (!e || !e.target) return;
      const { scrollTop, clientHeight } = e.target;
      throttledHandler(scrollTop, clientHeight, processedData.length);
    };
  }, [processedData.length]);

  const table = useReactTable({
    data: processedData,
    columns,
    state: {
      columnVisibility,
      columnOrder,
      sorting,
      grouping,
      columnSizing,
      columnSizingInfo,
      columnFilters,
    },
    onColumnVisibilityChange: setColumnVisibility,
    onColumnOrderChange: setColumnOrder,
    onSortingChange: setSorting,
    onGroupingChange: setGrouping,
    onColumnSizingChange: setColumnSizing,
    onColumnSizingInfoChange: setColumnSizingInfo,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getGroupedRowModel: getGroupedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    columnResizeMode: 'onChange',
    enableColumnResizing: true,
    autoResetExpanded: false, // Add this to maintain expanded state
  });

  // Handle column drag start
  const handleColumnDragStart = useCallback((e, columnId) => {
    // Prevent dragging if clicking on sort/group icons or resizer
    if (
      e.target.closest('.anticon-sort') ||
      e.target.closest('.anticon-bar-chart') ||
      e.target.closest('.column-resizer')
    ) {
      e.preventDefault();
      return;
    }

    // Also check if we're near the right edge of the header (resize area)
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const rightEdgeThreshold = 20; // pixels from right edge (increased for wider handle)

    if (x > rect.width - rightEdgeThreshold) {
      e.preventDefault();
      return;
    }

    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.target.innerHTML);
    setDraggedColumn(columnId);
  }, []);

  // Handle column drag over
  const handleColumnDragOver = useCallback(e => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  }, []);

  // Handle column drag enter
  const handleColumnDragEnter = useCallback(
    columnId => {
      if (draggedColumn && draggedColumn !== columnId) {
        setDragOverColumn(columnId);
      }
    },
    [draggedColumn],
  );

  // Handle column drag leave
  const handleColumnDragLeave = useCallback(() => {
    setDragOverColumn(null);
  }, []);

  // Handle column drop
  const handleColumnDrop = useCallback(
    (e, targetColumnId) => {
      e.preventDefault();

      if (!draggedColumn || draggedColumn === targetColumnId) {
        setDraggedColumn(null);
        setDragOverColumn(null);
        return;
      }

      // Get current column order
      const currentOrder =
        columnOrder.length > 0 ? columnOrder : table.getAllLeafColumns().map(c => c.id);
      const draggedIndex = currentOrder.indexOf(draggedColumn);
      const targetIndex = currentOrder.indexOf(targetColumnId);

      if (draggedIndex === -1 || targetIndex === -1) {
        setDraggedColumn(null);
        setDragOverColumn(null);
        return;
      }

      // Reorder columns
      const newOrder = [...currentOrder];
      const [removed] = newOrder.splice(draggedIndex, 1);
      newOrder.splice(targetIndex, 0, removed);

      setColumnOrder(newOrder);
      setDraggedColumn(null);
      setDragOverColumn(null);

      // Force re-render of visible rows
      setVisibleRange(prev => ({ ...prev }));
    },
    [draggedColumn, columnOrder, table],
  );

  // Handle column drag end
  const handleColumnDragEnd = useCallback(() => {
    setDraggedColumn(null);
    setDragOverColumn(null);
  }, []);

  // Optimized export functionality with chunking for large datasets
  const handleExportClick = useCallback(() => {
    const rows = table.getFilteredRowModel().rows;
    const visibleColumns = table.getVisibleLeafColumns();

    // Process in chunks to avoid blocking UI
    const processInChunks = async () => {
      const chunks = [];
      const chunkSize = 1000;

      // Headers
      chunks.push(visibleColumns.map(col => col.columnDef.header).join(','));

      // Process rows in chunks
      for (let i = 0; i < rows.length; i += chunkSize) {
        await new Promise(resolve => setTimeout(resolve, 0)); // Allow UI to update

        const chunk = rows.slice(i, Math.min(i + chunkSize, rows.length));
        const csvRows = chunk.map(row => {
          return visibleColumns
            .map(col => {
              const value = row.getValue(col.id);
              // Handle special characters and commas in CSV
              if (
                typeof value === 'string' &&
                (value.includes(',') || value.includes('"') || value.includes('\n'))
              ) {
                return `"${value.replace(/"/g, '""')}"`;
              }
              return value ?? '';
            })
            .join(',');
        });
        chunks.push(...csvRows);
      }

      const csvContent = chunks.join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);

      const fileName = `TimesheetSnapshot-${moment().format('YYYYMMDD-HHmm')}.csv`;

      link.setAttribute('href', url);
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    };

    processInChunks();
  }, [table]);

  // Helper function to get column label from column id
  const getColumnLabel = useCallback(columnId => {
    const column = ALL_COLUMNS.find(col => col.value === columnId);
    return column?.label || columnId;
  }, []);

  // Optimized data fetching with background processing
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const fetchedData = await fetchTimesheetsForPeriod({
        startDate,
        endDate,
        setLoadingText,
        query: GET_TIMESHEET_WEEKLY_SNAPSHOT,
      });

      // Process data in idle time for better performance
      if ('requestIdleCallback' in window) {
        requestIdleCallback(() => {
          const trimmedData = fetchedData.map(timesheet => ({
            ...timesheet,
            lineItems: timesheet.lineItems.map(lineItem => {
              const trimmed = { ...lineItem };
              ['monTask', 'tueTask', 'wedTask', 'thuTask', 'friTask', 'satTask', 'sunTask'].forEach(
                prop => {
                  if (
                    trimmed[prop]?.date &&
                    (trimmed[prop].date < startDate || trimmed[prop].date > endDate)
                  ) {
                    delete trimmed[prop];
                  }
                },
              );
              return trimmed;
            }),
          }));

          const gridData = createGridData(trimmedData);
          setRowData(gridData);
          setLoading(false);
        });
      } else {
        // Fallback for browsers without requestIdleCallback
        setTimeout(() => {
          const trimmedData = fetchedData.map(timesheet => ({
            ...timesheet,
            lineItems: timesheet.lineItems.map(lineItem => {
              const trimmed = { ...lineItem };
              ['monTask', 'tueTask', 'wedTask', 'thuTask', 'friTask', 'satTask', 'sunTask'].forEach(
                prop => {
                  if (
                    trimmed[prop]?.date &&
                    (trimmed[prop].date < startDate || trimmed[prop].date > endDate)
                  ) {
                    delete trimmed[prop];
                  }
                },
              );
              return trimmed;
            }),
          }));

          const gridData = createGridData(trimmedData);
          setRowData(gridData);
          setLoading(false);
        }, 0);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    fetchData().catch(console.error);
  }, [fetchData]);

  // Load overrides and saved reports from user settings
  useEffect(() => {
    const overridesVal = userSettings?.timesheetDailySnapshotOverrides;
    if (overridesVal && overridesVal !== '[]') {
      try {
        const parsedOverrides = JSON.parse(overridesVal);
        setOverrides(parsedOverrides);
        setUnsavedOverrides(parsedOverrides);
      } catch (e) {}
    }

    const reportsVal = userSettings?.timesheetDailySnapshotReports;
    if (reportsVal && reportsVal !== '[]') {
      try {
        const parsedReports = JSON.parse(reportsVal);
        setSavedReports(parsedReports);
      } catch (e) {}
    }
  }, [userSettings]);




  // Track unsaved changes
  useEffect(() => {
    setHasUnsavedChanges(JSON.stringify(overrides) !== JSON.stringify(unsavedOverrides));
  }, [overrides, unsavedOverrides]);

  // Manual save function for overrides
  const handleSaveOverrides = async () => {
    if (!userId) return;
    try {
      await client.mutate({
        mutation: UPDATE_USER_SETTINGS,
        variables: {
          id: userId,
          input: {
            timesheetDailySnapshotOverrides: JSON.stringify(overrides),
          },
        },
      });
      await refetch();
      setUnsavedOverrides(overrides);
      setHasUnsavedChanges(false);
    } catch (e) {
      console.error('Failed to save overrides:', e);
    }
  };

  // Check if current table state differs from active report
  const checkForUnsavedReportChanges = useCallback(() => {
    if (!activeReport?.tableState) {
      setHasUnsavedReportChanges(false);
      return false;
    }

    const currentState = {
      columnVisibility,
      columnOrder: columnOrder.length > 0 ? columnOrder : table.getAllColumns().map(c => c.id),
      sorting,
      grouping,
      columnSizing,
      columnFilters,
    };

    const savedState = activeReport.tableState;
    
    // Deep comparison of table states
    const hasChanges = 
      JSON.stringify(currentState.columnVisibility) !== JSON.stringify(savedState.columnVisibility) ||
      JSON.stringify(currentState.columnOrder) !== JSON.stringify(savedState.columnOrder) ||
      JSON.stringify(currentState.sorting) !== JSON.stringify(savedState.sorting) ||
      JSON.stringify(currentState.grouping) !== JSON.stringify(savedState.grouping) ||
      JSON.stringify(currentState.columnSizing) !== JSON.stringify(savedState.columnSizing) ||
      JSON.stringify(currentState.columnFilters) !== JSON.stringify(savedState.columnFilters);
    
    setHasUnsavedReportChanges(hasChanges);
    return hasChanges;
  }, [activeReport, columnVisibility, columnOrder, sorting, grouping, columnSizing, columnFilters, table]);

  // Check for unsaved changes whenever table state changes
  useEffect(() => {
    checkForUnsavedReportChanges();
  }, [checkForUnsavedReportChanges]);

  // Save report configuration
  const saveReportConfiguration = async (name, reportId = null) => {
    if (!userId) return;

    const newReport = {
      id: reportId || Date.now().toString(),
      name,
      createdAt: reportId ? activeReport?.createdAt : new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      tableState: {
        columnVisibility,
        columnOrder: columnOrder.length > 0 ? columnOrder : table.getAllColumns().map(c => c.id),
        sorting,
        grouping,
        columnSizing,
        columnFilters,
      },
    };

    const updatedReports = reportId 
      ? savedReports.map(r => r.id === reportId ? newReport : r)
      : [...savedReports.filter(r => r.name !== name), newReport];

    try {
      await client.mutate({
        mutation: UPDATE_USER_SETTINGS,
        variables: {
          id: userId,
          input: {
            timesheetDailySnapshotReports: JSON.stringify(updatedReports),
          },
        },
      });
      await refetch();
      setSavedReports(updatedReports);
      setActiveReport(newReport);
      setHasUnsavedReportChanges(false);
    } catch (e) {
      console.error('Failed to save report configuration:', e);
    }
  };

  // Load report configuration
  const loadReportConfiguration = report => {
    if (!report?.tableState) return;

    // Check for unsaved changes before loading
    if (hasUnsavedReportChanges) {
      modals.openConfirmModal({
        title: 'Unsaved Changes',
        children: (
          <div>
            <p>You have unsaved changes to the current report configuration.</p>
            <p>Do you want to discard these changes and load the selected report?</p>
          </div>
        ),
        labels: { confirm: 'Discard & Load', cancel: 'Cancel' },
        confirmProps: { color: 'red' },
        onConfirm: () => {
          setColumnVisibility(report.tableState.columnVisibility || {});
          setColumnOrder(report.tableState.columnOrder || []);
          setSorting(report.tableState.sorting || []);
          setGrouping(report.tableState.grouping || []);
          setColumnSizing(report.tableState.columnSizing || {});
          setColumnFilters(report.tableState.columnFilters || []);
          setActiveReport(report);
          setHasUnsavedReportChanges(false);
        },
      });
    } else {
      setColumnVisibility(report.tableState.columnVisibility || {});
      setColumnOrder(report.tableState.columnOrder || []);
      setSorting(report.tableState.sorting || []);
      setGrouping(report.tableState.grouping || []);
      setColumnSizing(report.tableState.columnSizing || {});
      setColumnFilters(report.tableState.columnFilters || []);
      setActiveReport(report);
      setHasUnsavedReportChanges(false);
    }
  };

  // Rename report
  const renameReport = async (reportId, newName) => {
    if (!userId) return;

    const updatedReports = savedReports.map(r => 
      r.id === reportId ? { ...r, name: newName, updatedAt: new Date().toISOString() } : r
    );

    try {
      await client.mutate({
        mutation: UPDATE_USER_SETTINGS,
        variables: {
          id: userId,
          input: {
            timesheetDailySnapshotReports: JSON.stringify(updatedReports),
          },
        },
      });
      await refetch();
      setSavedReports(updatedReports);
      if (activeReport?.id === reportId) {
        setActiveReport({ ...activeReport, name: newName, updatedAt: new Date().toISOString() });
      }
    } catch (e) {
      console.error('Failed to rename report:', e);
    }
  };

  // Delete report configuration
  const deleteReportConfiguration = async reportId => {
    if (!userId) return;

    modals.openConfirmModal({
      title: 'Delete Report',
      children: (
        <div>
          <p>Are you sure you want to delete this report?</p>
          <p className="text-sm text-gray-600 mt-2">
            Report: <strong>{savedReports.find(r => r.id === reportId)?.name}</strong>
          </p>
        </div>
      ),
      labels: { confirm: 'Delete', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        const updatedReports = savedReports.filter(r => r.id !== reportId);

        try {
          await client.mutate({
            mutation: UPDATE_USER_SETTINGS,
            variables: {
              id: userId,
              input: {
                timesheetDailySnapshotReports: JSON.stringify(updatedReports),
              },
            },
          });
          await refetch();
          setSavedReports(updatedReports);
          if (activeReport?.id === reportId) {
            setActiveReport(null);
            setHasUnsavedReportChanges(false);
          }
        } catch (e) {
          console.error('Failed to delete report configuration:', e);
        }
      },
    });
  };

  // Open save report modal
  const openSaveReportModal = (mode = 'new') => {
    if (mode === 'update' && activeReport) {
      // For update mode, show confirmation dialog directly
      modals.openConfirmModal({
        title: 'Confirm Save Changes',
        children: (
          <div>
            <p>Are you sure you want to save changes to "{activeReport.name}"?</p>
            <p className="text-sm text-gray-600 mt-2">This will update the saved report configuration.</p>
          </div>
        ),
        labels: { confirm: 'Save Changes', cancel: 'Cancel' },
        confirmProps: { color: 'blue' },
        onConfirm: () => {
          saveReportConfiguration(activeReport.name, activeReport.id);
        },
      });
    } else {
      // For new reports, show the modal to enter name
      setReportSaveMode(mode);
      setEditingReportTitle('');
      setReportTitleModalOpened(true);
    }
  };

  // Handle save report (only for new reports now)
  const handleSaveReport = () => {
    const trimmedTitle = editingReportTitle.trim();
    if (!trimmedTitle) return;

    // Check if name already exists
    const existingReport = savedReports.find(r => r.name === trimmedTitle);
    if (existingReport) {
      modals.openConfirmModal({
        title: 'Report Name Already Exists',
        children: (
          <div>
            <p>A report with the name "{trimmedTitle}" already exists.</p>
            <p>Do you want to overwrite it?</p>
          </div>
        ),
        labels: { confirm: 'Overwrite', cancel: 'Cancel' },
        confirmProps: { color: 'red' },
        onConfirm: () => {
          saveReportConfiguration(trimmedTitle);
          setReportTitleModalOpened(false);
        },
      });
    } else {
      saveReportConfiguration(trimmedTitle);
      setReportTitleModalOpened(false);
    }
  };

  return (
    <Card
      border
      title={activeReport ? `Custom Report: ${activeReport.name}` : "Custom Reports"}
      floating
      padded={false}
      actionComponent={
        <div className="flex flex-col gap-2">
          <div className="flex items-between w-full gap-4">
            <div className="flex gap-2">
              {dailySnapshotOverridesEnabled && (
                <Button variant="default" onClick={() => setModalOpened(true)}>
                  Manage Overrides
                </Button>
              )}
              <div className="flex items-center gap-2">
                <Menu shadow="md" width={250} position="bottom-end">
                  <Menu.Target>
                    <Button 
                      variant="default" 
                      rightIcon={<Icon type="down" />}
                      className={hasUnsavedReportChanges ? 'ring-2 ring-orange-400 ring-offset-1' : ''}
                    >
                      Reports {hasUnsavedReportChanges && <span className="ml-1 text-orange-600">•</span>}
                    </Button>
                  </Menu.Target>
                  <Menu.Dropdown>
                    {activeReport && hasUnsavedReportChanges && (
                      <>
                        <Menu.Label>Current Report</Menu.Label>
                        <Menu.Item
                          onClick={() => openSaveReportModal('update')}
                          icon={<Icon type="save" />}
                        >
                          Save Changes to "{activeReport.name}"
                        </Menu.Item>
                        <Menu.Item
                          onClick={() => openSaveReportModal('new')}
                          icon={<Icon type="file-add" />}
                        >
                          Save as New Report
                        </Menu.Item>
                        <Menu.Item
                          onClick={() => loadReportConfiguration(activeReport)}
                          icon={<Icon type="reload" />}
                          color="red"
                        >
                          Discard Changes
                        </Menu.Item>
                        <Menu.Divider />
                      </>
                    )}
                    <Menu.Label>Saved Reports</Menu.Label>
                    {savedReports.length === 0 ? (
                      <Menu.Item disabled>No saved reports</Menu.Item>
                    ) : (
                      <>
                        {savedReports
                          .sort((a, b) => {
                            // Sort by updatedAt or createdAt, most recent first
                            const dateA = new Date(a.updatedAt || a.createdAt);
                            const dateB = new Date(b.updatedAt || b.createdAt);
                            return dateB - dateA;
                          })
                          .map(report => (
                          <Menu 
                            key={report.id} 
                            trigger="hover" 
                            position="right-start" 
                            offset={2}
                            withinPortal
                          >
                            <Menu.Target>
                              <Menu.Item
                                rightSection={
                                  <div className="flex items-center gap-1">
                                    {activeReport?.id === report.id && (
                                      <Icon type="check" className="text-green-600" style={{ fontSize: '14px' }} />
                                    )}
                                    <Icon type="right" className="text-gray-400" style={{ fontSize: '10px' }} />
                                  </div>
                                }
                              >
                                <div className="flex flex-col items-start">
                                  <span className="font-medium">{report.name}</span>
                                  <span className="text-xs text-gray-500">
                                    {report.updatedAt 
                                      ? `Updated ${moment(report.updatedAt).fromNow()}`
                                      : `Created ${moment(report.createdAt).fromNow()}`
                                    }
                                  </span>
                                </div>
                              </Menu.Item>
                            </Menu.Target>
                            <Menu.Dropdown>
                              <Menu.Item
                                onClick={() => loadReportConfiguration(report)}
                                icon={<Icon type="file-text" />}
                              >
                                Load Report
                              </Menu.Item>
                              <Menu.Item
                                onClick={() => {
                                  setEditingReportTitle(report.name);
                                  modals.open({
                                    title: 'Rename Report',
                                    children: (
                                      <div>
                                        <TextInput
                                          label="Report Name"
                                          defaultValue={report.name}
                                          placeholder="Enter report name"
                                          data-autofocus
                                          onKeyDown={(e) => {
                                            if (e.key === 'Enter' && e.target.value.trim()) {
                                              renameReport(report.id, e.target.value.trim());
                                              modals.closeAll();
                                            }
                                          }}
                                        />
                                        <div className="flex justify-end gap-2 mt-4">
                                          <Button variant="default" onClick={() => modals.closeAll()}>
                                            Cancel
                                          </Button>
                                          <Button
                                            variant="filled"
                                            onClick={(e) => {
                                              const input = e.target.closest('.mantine-Modal-content').querySelector('input');
                                              if (input && input.value.trim()) {
                                                renameReport(report.id, input.value.trim());
                                                modals.closeAll();
                                              }
                                            }}
                                          >
                                            Rename
                                          </Button>
                                        </div>
                                      </div>
                                    ),
                                  });
                                }}
                                icon={<Icon type="edit" />}
                              >
                                Rename
                              </Menu.Item>
                              <Menu.Item
                                onClick={() => deleteReportConfiguration(report.id)}
                                icon={<Icon type="delete" />}
                                color="red"
                              >
                                Delete
                              </Menu.Item>
                            </Menu.Dropdown>
                          </Menu>
                        ))}
                      </>
                    )}
                    <Menu.Divider />
                    <Menu.Item
                      onClick={() => openSaveReportModal('new')}
                      icon={<Icon type="plus" />}
                    >
                      Save Current View as New
                    </Menu.Item>
                  </Menu.Dropdown>
                </Menu>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <DateRangePicker
                loading={loading ? true : undefined}
                handleDateChange={range => {
                  setStartDate(moment(range[0]).format('YYYYMMDD'));
                  setEndDate(moment(range[1]).format('YYYYMMDD'));
                }}
                startDate={startDate}
                endDate={endDate}
              />
              <div className="">
                <Button variant="default" disabled={loading} onClick={handleExportClick}>
                  Export
                </Button>
              </div>
            </div>
          </div>
          {/* Column visibility controls */}
          <div className="flex items-center gap-2 px-2">
            <ColumnSelector
              table={table}
              columnOrder={columnOrder}
              onColumnOrderChange={setColumnOrder}
            />
            <Button
              variant="default"
              onClick={() => {
                modals.openConfirmModal({
                  title: 'Reset to Default Settings',
                  children: (
                    <div>
                      <p>This will reset all table settings to their defaults:</p>
                      <ul className="list-disc ml-6 mt-2">
                        <li>Column visibility</li>
                        <li>Column order</li>
                        <li>Column widths</li>
                        <li>Sorting</li>
                        <li>Grouping</li>
                      </ul>
                      <p className="mt-2">Are you sure you want to continue?</p>
                    </div>
                  ),
                  labels: { confirm: 'Reset', cancel: 'Cancel' },
                  confirmProps: { color: 'red' },
                  onConfirm: () => {
                    // Reset all table state
                    const defaultVisible = [
                      'endDate',
                      'status',
                      'lastName',
                      'firstName',
                      'clientName',
                      'project',
                      'taskdate',
                      'hours',
                      'totalCost',
                    ];
                    table.getAllLeafColumns().forEach(col => {
                      col.toggleVisibility(defaultVisible.includes(col.id));
                    });
                    setColumnVisibility({});
                    setColumnOrder([]);
                    setSorting([]);
                    setGrouping([]);
                    setColumnSizing({});
                    table.resetColumnSizing();
                    setActiveReport(null);
                    setHasUnsavedReportChanges(false);
                  },
                });
              }}
              title="Reset all table settings to default"
            >
              Reset All
            </Button>
            {activeReport && hasUnsavedReportChanges && (
              <Button
                variant="filled"
                color="orange"
                size="sm"
                onClick={() => openSaveReportModal('update')}
                leftIcon={<Icon type="save" />}
              >
                Save Changes
              </Button>
            )}
            <span className="ml-auto text-xs text-gray-500">
              {processedData.length.toLocaleString()} rows
              {visibleRange.start > 0 &&
                ` (showing ${visibleRange.start + 1}-${Math.min(
                  visibleRange.end,
                  processedData.length,
                )})`}
            </span>
          </div>
          {/* Modal for overrides */}
          <Modal
            opened={modalOpened}
            onClose={() => {
              if (hasUnsavedChanges) {
                if (window.confirm('You have unsaved changes. Are you sure you want to close?')) {
                  setOverrides(unsavedOverrides);
                  setHasUnsavedChanges(false);
                  setModalOpened(false);
                }
              } else {
                setModalOpened(false);
              }
            }}
            title={
              <div className="flex items-center gap-2">
                <span>Field Overrides</span>
                {hasUnsavedChanges && (
                  <span className="text-sm text-orange-600 font-normal">(unsaved changes)</span>
                )}
              </div>
            }
            size="xxl"
            yOffset="2vh"
            overlayProps={{ blur: 2 }}
            className="[&>div]:!p-8"
            styles={{
              content: {
                maxHeight: '90vh',
                overflowY: 'auto',
                minWidth: 'min(1100px, 98vw)',
                maxWidth: '98vw',
              },
            }}
          >
            {/* Override management UI (same as original) */}
            <div className="flex justify-between mb-2 gap-2 ml-auto">
              <div className="flex flex-row w-full justify-between items-center gap-2">
                <div className="flex flex-row gap-2">
                  <Button
                    size="sm"
                    variant="default"
                    onClick={() => {
                      const data = JSON.stringify(overrides, null, 2);
                      const blob = new Blob([data], { type: 'application/json' });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = 'timesheet-daily-snapshot-overrides.json';
                      document.body.appendChild(a);
                      a.click();
                      setTimeout(() => {
                        document.body.removeChild(a);
                        URL.revokeObjectURL(url);
                      }, 0);
                    }}
                  >
                    Export Rules
                  </Button>
                  <Button
                    size="sm"
                    variant="default"
                    onClick={() => document.getElementById('import-overrides-input').click()}
                  >
                    Import Rules
                  </Button>
                  <input
                    id="import-overrides-input"
                    type="file"
                    accept="application/json"
                    style={{ display: 'none' }}
                    onChange={e => {
                      const file = e.target.files[0];
                      if (!file) return;
                      const reader = new FileReader();
                      reader.onload = event => {
                        try {
                          const imported = JSON.parse(event.target.result);
                          if (Array.isArray(imported)) {
                            if (
                              window.confirm(
                                'Are you sure you want to import and overwrite your current overrides?',
                              )
                            ) {
                              setOverrides(imported);
                            }
                          } else {
                            alert('Invalid format: JSON must be an array.');
                          }
                        } catch (err) {
                          alert('Failed to import: Invalid JSON.');
                        }
                      };
                      reader.readAsText(file);
                      e.target.value = '';
                    }}
                  />
                </div>
                <div className="flex flex-row gap-2 ml-auto">
                  <Button
                    variant="default"
                    onClick={() => {
                      // Open Mantine modals manager modal for adding a new rule
                      let tempConditions = [{ field: '', value: '' }];
                      let tempOverrideFields = [{ field: '', value: '' }];
                      modals.open({
                        title: 'Add New Override Rule',
                        size: 'lg',
                        children: (
                          <OverrideRuleEditor
                            initialConditions={tempConditions}
                            initialOverrideFields={tempOverrideFields}
                            onSave={(conditions, overrideFields) => {
                              const overrideObj = {};
                              overrideFields.forEach(o => {
                                if (o.field && o.value !== undefined && o.value !== '') {
                                  overrideObj[o.field] = o.value;
                                }
                              });
                              setOverrides(prev => [
                                ...prev,
                                {
                                  conditions: conditions.filter(c => c.field && c.value),
                                  override: overrideObj,
                                },
                              ]);
                              modals.closeAll();
                            }}
                          />
                        ),
                      });
                    }}
                  >
                    Add
                  </Button>
                </div>
              </div>
            </div>
            <div className="mt-2 relative">
              {overrides.length > 0 ? (
                <Table
                  verticalSpacing="xs"
                  horizontalSpacing="xs"
                  fontSize="xs"
                  className="min-w-full"
                  withBorder
                >
                  <thead>
                    <tr className="bg-gray-200">
                      <th className="rounded-tl whitespace-nowrap">Conditions</th>
                      <th className="whitespace-nowrap">Overrides</th>
                      <th className="rounded-tr text-right whitespace-nowrap"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {overrides.map((ovr, ovrIdx) => (
                      <tr
                        key={`ovr-${ovrIdx}`}
                        className="bg-white rounded border hover:bg-blue-50 transition"
                        style={{ boxShadow: 'none' }}
                      >
                        <td className="text-gray-700 align-top whitespace-nowrap">
                          <div className="flex flex-col gap-0.5">
                            {ovr.conditions.map((c, condIdx) => (
                              <div key={condIdx}>
                                <span className="font-bold text-blue-900">
                                  {getColumnLabel(c.field)}:
                                </span>{' '}
                                {c.value}
                              </div>
                            ))}
                          </div>
                        </td>
                        <td className="align-top whitespace-nowrap">
                          <div className="flex flex-col gap-0.5">
                            {Object.entries(ovr.override).map(([field, value], idx) => (
                              <div key={idx} className="whitespace-nowrap">
                                <span className="font-bold text-blue-900">
                                  {getColumnLabel(field)}:
                                </span>{' '}
                                <span className="text-blue-700">{value}</span>
                              </div>
                            ))}
                          </div>
                        </td>
                        <td className="text-right align-top">
                          <div className="flex flex-row justify-end gap-1">
                            <ActionIcon
                              color="gray"
                              variant="subtle"
                              onClick={() => moveOverride(ovrIdx, ovrIdx - 1)}
                              disabled={ovrIdx === 0}
                              title="Move Up"
                              className="hover:bg-gray-100 hover:text-gray-700 transition mr-1"
                              style={{ minHeight: 28, minWidth: 28 }}
                            >
                              <span className="text-base font-bold">↑</span>
                            </ActionIcon>
                            <ActionIcon
                              color="gray"
                              variant="subtle"
                              onClick={() => moveOverride(ovrIdx, ovrIdx + 1)}
                              disabled={ovrIdx === overrides.length - 1}
                              title="Move Down"
                              className="hover:bg-gray-100 hover:text-gray-700 transition mr-1"
                              style={{ minHeight: 28, minWidth: 28 }}
                            >
                              <span className="text-base font-bold">↓</span>
                            </ActionIcon>
                            <ActionIcon
                              color="blue"
                              variant="subtle"
                              onClick={() => {
                                // Open modal to edit this rule
                                modals.open({
                                  title: 'Edit Override Rule',
                                  size: 'lg',
                                  children: (
                                    <OverrideRuleEditor
                                      initialConditions={ovr.conditions.map(c => ({ ...c }))}
                                      initialOverrideFields={Object.entries(ovr.override).map(
                                        ([field, value]) => ({ field, value }),
                                      )}
                                      onSave={(conditions, overrideFields) => {
                                        const overrideObj = {};
                                        overrideFields.forEach(o => {
                                          if (o.field && o.value !== undefined && o.value !== '') {
                                            overrideObj[o.field] = o.value;
                                          }
                                        });
                                        setOverrides(prev => [
                                          ...prev.slice(0, ovrIdx),
                                          {
                                            conditions: conditions.filter(c => c.field && c.value),
                                            override: overrideObj,
                                          },
                                          ...prev.slice(ovrIdx + 1),
                                        ]);
                                        modals.closeAll();
                                      }}
                                    />
                                  ),
                                });
                              }}
                              title="Edit Override"
                              className="hover:bg-blue-100 hover:text-blue-700 transition mr-1"
                              style={{ minHeight: 28, minWidth: 28 }}
                            >
                              <Icon type="edit" style={{ fontSize: 16 }} />
                            </ActionIcon>
                            <ActionIcon
                              color="gray"
                              variant="subtle"
                              onClick={() => {
                                // Duplicate rule and open edit modal for the new rule
                                const newConditions = ovr.conditions.map(c => ({ ...c }));
                                const newOverrideFields = Object.entries(ovr.override).map(
                                  ([field, value]) => ({ field, value }),
                                );
                                // Insert duplicate after current
                                setOverrides(prev => {
                                  const newRule = {
                                    conditions: newConditions,
                                    override: { ...ovr.override },
                                  };
                                  const newOverrides = [
                                    ...prev.slice(0, ovrIdx + 1),
                                    newRule,
                                    ...prev.slice(ovrIdx + 1),
                                  ];
                                  // Open modal for the new rule after state update
                                  setTimeout(() => {
                                    modals.open({
                                      title: 'Edit Duplicated Override Rule',
                                      size: 'lg',
                                      children: (
                                        <OverrideRuleEditor
                                          initialConditions={newConditions.map(c => ({ ...c }))}
                                          initialOverrideFields={newOverrideFields.map(f => ({
                                            ...f,
                                          }))}
                                          onSave={(conditions, overrideFields) => {
                                            const overrideObj = {};
                                            overrideFields.forEach(o => {
                                              if (
                                                o.field &&
                                                o.value !== undefined &&
                                                o.value !== ''
                                              ) {
                                                overrideObj[o.field] = o.value;
                                              }
                                            });
                                            setOverrides(current => {
                                              const idx = ovrIdx + 1;
                                              return [
                                                ...current.slice(0, idx),
                                                {
                                                  conditions: conditions.filter(
                                                    c => c.field && c.value,
                                                  ),
                                                  override: overrideObj,
                                                },
                                                ...current.slice(idx + 1),
                                              ];
                                            });
                                            modals.closeAll();
                                          }}
                                        />
                                      ),
                                    });
                                  }, 0);
                                  return newOverrides;
                                });
                              }}
                              title="Duplicate Override"
                              className="hover:bg-gray-100 hover:text-gray-700 transition mr-1"
                              style={{ minHeight: 28, minWidth: 28 }}
                            >
                              <Icon type="copy" style={{ fontSize: 16 }} />
                            </ActionIcon>
                            <ActionIcon
                              color="red"
                              variant="subtle"
                              onClick={() => {
                                if (
                                  window.confirm(
                                    'Are you sure you want to delete this override rule?',
                                  )
                                ) {
                                  setOverrides(prev => prev.filter((_, i) => i !== ovrIdx));
                                }
                              }}
                              title="Remove Override"
                              className="hover:bg-red-100 hover:text-red-700 transition"
                              style={{ minHeight: 28, minWidth: 28 }}
                            >
                              <span className="text-base font-bold">×</span>
                            </ActionIcon>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              ) : (
                <div className="text-gray-500 text-xs p-2">No overrides set.</div>
              )}
              {/* Sticky footer for Clear All and Save buttons */}
              <div
                className="flex flex-row justify-between gap-2 p-2 bg-white border-t sticky"
                style={{ bottom: 0, left: 0, right: 0, zIndex: 10 }}
              >
                <Button
                  variant="default"
                  onClick={() => {
                    if (window.confirm('Are you sure you want to clear all overrides?')) {
                      setOverrides([]);
                    }
                  }}
                >
                  Clear All
                </Button>
                <div className="flex gap-2">
                  {hasUnsavedChanges && (
                    <Button
                      variant="default"
                      onClick={() => {
                        setOverrides(unsavedOverrides);
                        setHasUnsavedChanges(false);
                      }}
                    >
                      Revert Changes
                    </Button>
                  )}
                  <Button
                    variant="filled"
                    disabled={!hasUnsavedChanges}
                    onClick={handleSaveOverrides}
                  >
                    Save Changes
                  </Button>
                </div>
              </div>
            </div>
          </Modal>

          {/* Report Title Modal */}
          <Modal
            opened={reportTitleModalOpened}
            onClose={() => setReportTitleModalOpened(false)}
            title={reportSaveMode === 'update' ? 'Update Report' : 'Save Report'}
            size="sm"
            centered
          >
            <div>
              <TextInput
                label="Report Name"
                value={editingReportTitle}
                onChange={(e) => setEditingReportTitle(e.target.value)}
                placeholder="Enter a descriptive name for this report"
                description={reportSaveMode === 'update' 
                  ? 'Update the name for the current report' 
                  : 'This report will save the current table configuration'}
                data-autofocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && editingReportTitle.trim()) {
                    handleSaveReport();
                  }
                }}
              />
              <div className="flex justify-end gap-2 mt-4">
                <Button variant="default" onClick={() => setReportTitleModalOpened(false)}>
                  Cancel
                </Button>
                <Button
                  variant="filled"
                  onClick={handleSaveReport}
                  disabled={!editingReportTitle.trim()}
                >
                  {reportSaveMode === 'update' ? 'Update' : 'Save'}
                </Button>
              </div>
            </div>
          </Modal>
        </div>
      }
    >
      {loading ? (
        <TextLoader text={loadingText} />
      ) : (
        <TableWrapper ref={scrollContainerRef} onScroll={handleScroll}>
          <table
            className={columnSizingInfo.isResizingColumn ? 'resizing-active' : ''}
            key={columnOrder.join(',')}
          >
            <colgroup>
              {table.getVisibleLeafColumns().map(column => (
                <col key={column.id} style={{ width: `${column.getSize()}px` }} />
              ))}
            </colgroup>
            <thead>
              {table.getHeaderGroups().map(headerGroup => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map(header => (
                    <th
                      key={header.id}
                      style={{
                        width: `${header.getSize()}px`,
                        minWidth: `${header.getSize()}px`,
                        maxWidth: `${header.getSize()}px`,
                        position: 'relative',
                      }}
                      className={`
                        ${draggedColumn === header.column.id ? 'dragging' : ''}
                        ${dragOverColumn === header.column.id ? 'drag-over' : ''}
                      `}
                      draggable={!header.isPlaceholder && !columnSizingInfo.isResizingColumn}
                      onDragStart={e => handleColumnDragStart(e, header.column.id)}
                      onDragOver={handleColumnDragOver}
                      onDragEnter={() => handleColumnDragEnter(header.column.id)}
                      onDragLeave={handleColumnDragLeave}
                      onDrop={e => handleColumnDrop(e, header.column.id)}
                      onDragEnd={handleColumnDragEnd}
                    >
                      {header.isPlaceholder ? null : (
                        <>
                          <div
                            className={
                              header.column.getCanSort()
                                ? 'sortable-header flex items-center gap-1'
                                : 'flex items-center gap-1'
                            }
                          >
                            <span
                              onClick={header.column.getToggleSortingHandler()}
                              style={{ flex: 1 }}
                            >
                              {flexRender(header.column.columnDef.header, header.getContext())}
                            </span>
                            {header.column.getCanSort() && (
                              <Tooltip
                                title={
                                  header.column.getIsSorted()
                                    ? `Sorted ${
                                        header.column.getIsSorted() === 'asc'
                                          ? 'ascending'
                                          : 'descending'
                                      }. Click to ${
                                        header.column.getIsSorted() === 'asc'
                                          ? 'sort descending'
                                          : 'clear sort'
                                      }`
                                    : `Sort by ${header.column.columnDef.header}`
                                }
                                placement="top"
                                overlayStyle={{
                                  fontSize: '14px',
                                  maxWidth: '300px',
                                }}
                                overlayClassName="custom-tooltip-overlay"
                              >
                                <Icon
                                  type={
                                    header.column.getIsSorted()
                                      ? header.column.getIsSorted() === 'asc'
                                        ? 'sort-ascending'
                                        : 'sort-descending'
                                      : 'sort-ascending'
                                  }
                                  style={{
                                    fontSize: '12px',
                                    marginLeft: '4px',
                                    cursor: 'pointer',
                                  }}
                                  onClick={header.column.getToggleSortingHandler()}
                                  className={`anticon-sort sort-icon ${
                                    header.column.getIsSorted() ? 'sorted' : ''
                                  }`}
                                  draggable={false}
                                  onMouseDown={e => e.stopPropagation()}
                                />
                              </Tooltip>
                            )}
                            {header.column.getCanGroup() && (
                              <Tooltip
                                title={
                                  grouping.includes(header.column.id)
                                    ? `Remove grouping by ${header.column.columnDef.header}`
                                    : `Group by ${header.column.columnDef.header}`
                                }
                                placement="top"
                                overlayStyle={{
                                  fontSize: '14px',
                                  maxWidth: '300px',
                                }}
                                overlayClassName="custom-tooltip-overlay"
                              >
                                <Icon
                                  type="bar-chart"
                                  onClick={e => {
                                    e.stopPropagation();
                                    const isGrouped = grouping.includes(header.column.id);
                                    if (isGrouped) {
                                      // Remove from grouping
                                      setGrouping(grouping.filter(g => g !== header.column.id));
                                    } else {
                                      // Add to grouping
                                      setGrouping([...grouping, header.column.id]);
                                    }
                                  }}
                                  style={{
                                    fontSize: '12px',
                                    marginLeft: '4px',
                                    cursor: 'pointer',
                                  }}
                                  className={grouping.includes(header.column.id) ? 'active' : ''}
                                  draggable={false}
                                  onMouseDown={e => e.stopPropagation()}
                                />
                              </Tooltip>
                            )}
                            {header.column.getCanFilter() && (
                              <FilterPopover column={header.column} />
                            )}
                          </div>

                          {/* Column resizer */}
                          {header.column.getCanResize() && (
                            <div
                              onMouseDown={e => {
                                e.stopPropagation();
                                e.preventDefault();
                                header.getResizeHandler()(e);
                              }}
                              onTouchStart={e => {
                                e.stopPropagation();
                                header.getResizeHandler()(e);
                              }}
                              onDoubleClick={() => {
                                header.column.resetSize();
                              }}
                              className={`column-resizer ${
                                header.column.getIsResizing() ? 'resizing' : ''
                              }`}
                            />
                          )}
                        </>
                      )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {(() => {
                const rows = table.getRowModel().rows;

                // Render only visible rows with virtual scrolling
                return (
                  <>
                    {/* Virtual spacer for rows before visible range */}
                    {visibleRange.start > 0 && (
                      <tr>
                        <td
                          colSpan={table.getVisibleLeafColumns().length}
                          style={{ height: visibleRange.start * ROW_HEIGHT }}
                        />
                      </tr>
                    )}

                    {/* Render only visible rows */}
                    {rows.slice(visibleRange.start, visibleRange.end).map(row => (
                      <TableRow key={row.id} row={row} />
                    ))}

                    {/* Virtual spacer for rows after visible range */}
                    {visibleRange.end < rows.length && (
                      <tr>
                        <td
                          colSpan={table.getVisibleLeafColumns().length}
                          style={{ height: (rows.length - visibleRange.end) * ROW_HEIGHT }}
                        />
                      </tr>
                    )}
                  </>
                );
              })()}
            </tbody>
          </table>
        </TableWrapper>
      )}
    </Card>
  );
};

export default CustomReports;
